import React from 'react';
import Typography from '@material-ui/core/Typography';
import useFetchWithStatus from 'hooks/fetchWithStatus';
import strings from 'strings';
import { FPCard, FPCardContent, FPCardHeader } from 'UI/components/atoms/FPCard';
import { When } from 'UI/components/atoms/When';
import EmptyPlaceholder from 'UI/components/templates/EmptyPlaceholder';
import { FeatureFlags } from 'UI/constants/featureFlags';
import { UIStatus } from 'UI/constants/status';
import { capitalizeFirstLetter, getFeatureFlags } from 'UI/utils';

import FunctionalTitlesList from './FunctionalTitlesList/FunctionalTitlesList';
import { Keywords } from './Keywords';
import PilBreadcrumb from './PilBreadcrumb';
import { useStyles } from './styles';

/**
 * @param {Object} props
 * @param {Object} props.selectionParams
 */
const PilInformation = ({
  selectionParams: {
    id,
    title,
    type = 'industry',
    informationEndpoint,
    functionalTitlesEndpoint,
    showFunctionalTitles
  }
}) => {
  const showPilDescription = getFeatureFlags().includes(FeatureFlags.PilDescriptions);
  const classes = useStyles();

  const {
    Status,
    state: { status, results: pilResults }
  } = useFetchWithStatus(informationEndpoint, null, UIStatus.Default);
  const isLoading = status === UIStatus.Loading;
  const hasKeywords = pilResults?.keywordTags && pilResults.keywordTags.length > 0;

  return (
    <FPCard className={classes.cardContainer}>
      <FPCardHeader
        title={title || strings.pil.secondTitle}
        subheader={
          <>
            {showPilDescription ? (
              <PilBreadcrumb type={type} isLoading={isLoading} pilResults={pilResults} />
            ) : (
              capitalizeFirstLetter(type)
            )}
          </>
        }
      />
      <FPCardContent className={classes.contentContainer}>
        <Status
          loading={null}
          default={<EmptyPlaceholder subtitle={strings.pil.instructions} />}
          success={results => (
            <When condition={id && showPilDescription}>
              <PilDescription type={type} description={results?.description} />
            </When>
          )}
          error={error => (
            <When condition={showPilDescription}>
              <Typography paragraph gutterBottom>
                {error}
              </Typography>
            </When>
          )}
          empty={<EmptyPlaceholder subtitle={strings.pil.instructions} />}
        />

        <When condition={!isLoading && hasKeywords}>
          <Keywords keywordTags={pilResults?.keywordTags} />
        </When>

        <When condition={showFunctionalTitles}>
          <FunctionalTitlesList endpoint={functionalTitlesEndpoint} />
        </When>

        <When condition={!showFunctionalTitles && status !== UIStatus.Default}>
          <EmptyPlaceholder subtitle={strings.pil.functionalTitles.instructions} />
        </When>
      </FPCardContent>
    </FPCard>
  );
};

const PilDescription = ({ type, description }) => {
  const classes = useStyles();

  if (!description) return null;

  return (
    <div className={classes.descriptionContainer}>
      <Typography variant="subtitle1" gutterBottom>
        {strings.formatString(strings.pil.description, {
          pil: capitalizeFirstLetter(type ?? '')
        })}
      </Typography>
      <Typography className={classes.descripton}>{description}</Typography>
    </div>
  );
};

export default PilInformation;
